import { createEffect, onCleanup, onMount } from "solid-js";

export function Sparkline(props: { data: () => readonly number[]; color?: string }) {
  let canvas!: HTMLCanvasElement;

  function draw() {
    const data = props.data();
    if (!data.length) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    const dpr = window.devicePixelRatio || 1;
    const w = canvas.clientWidth;
    const h = canvas.clientHeight;
    canvas.width = w * dpr;
    canvas.height = h * dpr;
    ctx.scale(dpr, dpr);
    ctx.clearRect(0, 0, w, h);

    const min = Math.min(...data);
    const max = Math.max(...data);
    const range = max - min || 1;
    const color = props.color ?? "#38bdf8";

    // Build path
    ctx.beginPath();
    data.forEach((v, i) => {
      const x = (i / Math.max(1, data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h * 0.85) - h * 0.075;
      if (i === 0) ctx.moveTo(x, y);
      else ctx.lineTo(x, y);
    });

    // Gradient fill under the curve
    const fillPath = new Path2D();
    data.forEach((v, i) => {
      const x = (i / Math.max(1, data.length - 1)) * w;
      const y = h - ((v - min) / range) * (h * 0.85) - h * 0.075;
      if (i === 0) fillPath.moveTo(x, y);
      else fillPath.lineTo(x, y);
    });
    fillPath.lineTo(w, h);
    fillPath.lineTo(0, h);
    fillPath.closePath();

    const grad = ctx.createLinearGradient(0, 0, 0, h);
    grad.addColorStop(0, color + "30");
    grad.addColorStop(0.7, color + "08");
    grad.addColorStop(1, "transparent");
    ctx.fillStyle = grad;
    ctx.fill(fillPath);

    // Stroke the line
    ctx.strokeStyle = color;
    ctx.lineWidth = 1.5;
    ctx.lineJoin = "round";
    ctx.lineCap = "round";
    ctx.stroke();

    // Glow on the line
    ctx.strokeStyle = color + "40";
    ctx.lineWidth = 4;
    ctx.filter = "blur(3px)";
    ctx.stroke();
    ctx.filter = "none";

    // Latest value dot
    if (data.length > 1) {
      const lastX = w;
      const lastY = h - ((data[data.length - 1]! - min) / range) * (h * 0.85) - h * 0.075;
      ctx.beginPath();
      ctx.arc(lastX, lastY, 2.5, 0, Math.PI * 2);
      ctx.fillStyle = color;
      ctx.fill();
      ctx.beginPath();
      ctx.arc(lastX, lastY, 5, 0, Math.PI * 2);
      ctx.fillStyle = color + "20";
      ctx.fill();
    }
  }

  onMount(draw);
  createEffect(draw);

  const ro = new ResizeObserver(draw);
  onMount(() => ro.observe(canvas));
  onCleanup(() => ro.disconnect());

  return <canvas ref={canvas} class="h-full w-full" />;
}
